import React, { useState } from "react";
import { Modal, Form, Input, Checkbox, Row, Col, Typography, Space, Divider, Button, Avatar, Upload, Switch } from "antd";
import {
    UserAddOutlined, LockOutlined, MailOutlined, PhoneOutlined,
    UserOutlined, SafetyOutlined, EditOutlined, UploadOutlined,
    CheckOutlined, CloseOutlined
} from "@ant-design/icons";
import AdminServ from "../service/AdminServ";
import Notify from "../util/Notify";
import { getImageUrl } from "./SafeImage";

const { Text, Title } = Typography;

const adminPermissionsList = [
    { label: "Manage Users", value: "manage_users" },
    { label: "Manage Sellers", value: "manage_sellers" },
    { label: "Manage Products", value: "manage_products" },
    { label: "Manage Orders", value: "manage_orders" },
    { label: "Manage Categories", value: "manage_categories" },
    { label: "Manage Messages", value: "manage_messages" },
    { label: "Manage Credits", value: "manage_credits" },
    { label: "Manage Banners", value: "manage_banners" },
    { label: "Manage Brands", value: "manage_brands" },
    { label: "Full Access", value: "full_access" },
];

const AddAdminModal = ({ open, onClose, refresh, admin = null }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [profileImage, setProfileImage] = useState(null);

    React.useEffect(() => {
        if (open) {
            if (admin) {
                form.setFieldsValue({
                    firstName: admin.firstName,
                    lastName: admin.lastName,
                    email: admin.email,
                    phone: admin.phone,
                    isActive: admin.isActive ?? true,
                    isBlocked: admin.isBlocked ?? false,
                    password: ''
                });
                setSelectedPermissions(admin.permissions || []);
                setProfileImage(admin.profilePhoto || admin.profileImage);
            } else {
                form.resetFields();
                form.setFieldsValue({ isActive: true, isBlocked: false });
                setSelectedPermissions([]);
                setProfileImage(null);
            }
        }
    }, [open, admin, form]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const payload = {
                ...values,
                permissions: selectedPermissions,
                profilePhoto: profileImage
            };

            if (admin) {
                if (!payload.password) delete payload.password;
                await AdminServ.updateAdmin(admin._id, payload);
                Notify.success("Admin updated successfully");
            } else {
                await AdminServ.createAdmin(payload);
                Notify.success("Admin created successfully");
            }

            onClose();
            refresh();
        } catch (err) {
            if (err.name !== "ValidationError") {
                Notify.error(err, `Failed to ${admin ? 'update' : 'create'} admin`);
            }
        } finally {
            setLoading(false);
        }
    };

    const labelStyle = { 
        fontSize: '11px', 
        fontWeight: 800, 
        color: '#4f46e5', 
        textTransform: 'uppercase', 
        letterSpacing: '0.4px',
        marginBottom: '8px',
        display: 'block'
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={680}
            closable={false}
            className="premium-modal-v2"
            bodyStyle={{ padding: 0 }}
            centered
        >
            {/* Header - Matching Image 2 */}
            <div style={{ 
                padding: '20px 24px', 
                background: '#fff',
                borderBottom: '1.5px solid #f8fafc',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderRadius: '24px 24px 0 0'
            }}>
                <div>
                    <Title level={5} style={{ margin: 0, color: '#0f172a', fontWeight: 900, fontSize: '18px', letterSpacing: '-0.5px' }}>
                        {admin ? 'Edit Administrator Details' : 'Create New Administrator'}
                    </Title>
                    <Text type="secondary" style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                        {admin ? 'Update administrator account information' : 'Configure a new administrative account'}
                    </Text>
                </div>
                <Button 
                    type="text" 
                    icon={<CloseOutlined style={{ fontSize: '14px', color: '#94a3b8' }} />} 
                    onClick={onClose}
                    style={{ background: '#f8fafc', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                />
            </div>

            {/* Scrollable Body */}
            <div style={{ 
                maxHeight: '70vh', 
                overflowY: 'auto', 
                padding: '24px',
                scrollbarWidth: 'thin'
            }}>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ isActive: true, isBlocked: false }}
                >
                    <Row gutter={40}>
                        {/* Left Side: Avatar - Matching Image 2 with Fallback */}
                        <Col span={8}>
                            <div style={{ textAlign: 'center' }}>
                                <Avatar 
                                    size={120} 
                                    src={profileImage ? getImageUrl(profileImage) : null}
                                    icon={<UserOutlined />}
                                    style={{ 
                                        border: '1px solid #e2e8f0', 
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                        background: '#f8fafc',
                                        marginBottom: '16px'
                                    }}
                                />
                                <Upload
                                    showUploadList={false}
                                    beforeUpload={() => false}
                                    onChange={() => Notify.info("Image upload functionality would go here")}
                                >
                                    <Button 
                                        type="text"
                                        icon={<UploadOutlined style={{ fontSize: '14px' }} />} 
                                        style={{ 
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', 
                                            margin: '0 auto', color: '#1e293b', fontWeight: 700, fontSize: '12px',
                                            gap: '8px', padding: '4px 12px', background: '#f8fafc', borderRadius: '8px'
                                        }}
                                    >
                                        Upload Photo
                                    </Button>
                                </Upload>
                                <div style={{ marginTop: '8px' }}>
                                    <Text type="secondary" style={{ fontSize: '10px' }}>Recommended: 200x200px</Text>
                                </div>
                            </div>
                        </Col>

                        {/* Right Side: Primary Info - Matching Image 2 */}
                        <Col span={16}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <label style={labelStyle}>FIRST NAME <span style={{ color: '#ef4444' }}>*</span></label>
                                    <Form.Item name="firstName" rules={[{ required: true, message: 'Required' }]} style={{ marginBottom: '20px' }}>
                                        <Input placeholder="Super" style={{ height: '38px', borderRadius: '6px', fontSize: '13px', border: '1.5px solid #f1f5f9' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <label style={labelStyle}>LAST NAME <span style={{ color: '#ef4444' }}>*</span></label>
                                    <Form.Item name="lastName" rules={[{ required: true, message: 'Required' }]} style={{ marginBottom: '20px' }}>
                                        <Input placeholder="Admin" style={{ height: '38px', borderRadius: '6px', fontSize: '13px', border: '1.5px solid #f1f5f9' }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <label style={labelStyle}>EMAIL ADDRESS <span style={{ color: '#ef4444' }}>*</span></label>
                                    <Form.Item name="email" rules={[{ required: true, type: 'email', message: 'Required' }]} style={{ marginBottom: '20px' }}>
                                        <Input placeholder="nearzon@gmail.com" style={{ height: '38px', borderRadius: '6px', fontSize: '13px', border: '1.5px solid #f1f5f9' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <label style={labelStyle}>PHONE NUMBER <span style={{ color: '#ef4444' }}>*</span></label>
                                    <Form.Item name="phone" rules={[{ required: true, message: 'Required' }]} style={{ marginBottom: '20px' }}>
                                        <Input placeholder="7094686155" style={{ height: '38px', borderRadius: '6px', fontSize: '13px', border: '1.5px solid #f1f5f9' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            
                            <label style={labelStyle}>{admin ? "CHANGE PASSWORD (OPTIONAL)" : "SET PASSWORD *"}</label>
                            <Form.Item name="password" rules={[{ required: !admin, min: 6, message: 'Min 6 chars' }]} style={{ marginBottom: '12px' }}>
                                <Input.Password 
                                    placeholder={admin ? "Leave blank to keep current" : "Enter password"} 
                                    style={{ height: '38px', borderRadius: '6px', fontSize: '13px', border: '1.5px solid #f1f5f9' }} 
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider style={{ margin: '12px 0 24px 0', borderTop: '1.5px solid #f8fafc' }} />

                    {/* Status Toggles - High Spacing */}
                    <Row gutter={24} style={{ marginBottom: '24px' }}>
                        <Col span={12}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', padding: '0 4px' }}>
                                <div>
                                    <label style={{ ...labelStyle, marginBottom: '2px' }}>Active Status</label>
                                    <Text type="secondary" style={{ fontSize: '11px' }}>Enable account access</Text>
                                </div>
                                <Form.Item name="isActive" valuePropName="checked" style={{ margin: 0 }}>
                                    <Switch size="small" style={{ background: '#4f46e5' }} />
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', padding: '0 4px' }}>
                                <div>
                                    <label style={{ ...labelStyle, marginBottom: '2px', color: '#dc2626' }}>Block Status</label>
                                    <Text type="secondary" style={{ fontSize: '11px' }}>Restrict account access</Text>
                                </div>
                                <Form.Item name="isBlocked" valuePropName="checked" style={{ margin: 0 }}>
                                    <Switch size="small" danger />
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>

                    {/* Permissions - Grid Style */}
                    <div style={{ marginBottom: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <label style={{ ...labelStyle, margin: 0 }}>Assign Module Permissions</label>
                            <Button
                                size="small"
                                type="text"
                                onClick={() => {
                                    if (selectedPermissions.length === adminPermissionsList.length) {
                                        setSelectedPermissions([]);
                                    } else {
                                        setSelectedPermissions(adminPermissionsList.map(p => p.value));
                                    }
                                }}
                                style={{ color: '#4f46e5', fontWeight: 800, fontSize: '11px' }}
                            >
                                {selectedPermissions.length === adminPermissionsList.length ? "UNSELECT ALL" : "SELECT ALL"}
                            </Button>
                        </div>

                        <Checkbox.Group
                            style={{ width: '100%' }}
                            value={selectedPermissions}
                            onChange={setSelectedPermissions}
                        >
                            <Row gutter={[8, 8]}>
                                {adminPermissionsList.map((permission) => (
                                    <Col span={8} key={permission.value}>
                                        <div 
                                            style={{
                                                padding: '10px 12px', borderRadius: '8px', border: '1.5px solid',
                                                borderColor: selectedPermissions.includes(permission.value) ? '#4f46e5' : '#f1f5f9',
                                                background: selectedPermissions.includes(permission.value) ? '#f5f3ff' : '#fff',
                                                cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center'
                                            }}
                                            onClick={() => {
                                                if (selectedPermissions.includes(permission.value)) {
                                                    setSelectedPermissions(selectedPermissions.filter(p => p !== permission.value));
                                                } else {
                                                    setSelectedPermissions([...selectedPermissions, permission.value]);
                                                }
                                            }}
                                        >
                                            <Checkbox value={permission.value} onClick={(e) => e.stopPropagation()} style={{ transform: 'scale(0.9)' }} />
                                            <span style={{ 
                                                marginLeft: '8px', fontSize: '11px', fontWeight: selectedPermissions.includes(permission.value) ? 700 : 600,
                                                color: selectedPermissions.includes(permission.value) ? '#4f46e5' : '#64748b'
                                            }}>
                                                {permission.label}
                                            </span>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </Checkbox.Group>
                    </div>
                </Form>
            </div>

            {/* Footer - Solid Buttons Matching Image 2 */}
            <div style={{ 
                padding: '16px 24px', 
                borderTop: '1.5px solid #f8fafc',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                background: '#fff',
                borderRadius: '0 0 24px 24px'
            }}>
                <Button 
                    onClick={onClose}
                    style={{ 
                        height: '38px', padding: '0 24px', borderRadius: '10px',
                        fontWeight: 700, fontSize: '13px', color: '#64748b', border: '1.5px solid #e2e8f0',
                        background: '#fff'
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    type="primary"
                    onClick={handleOk}
                    loading={loading}
                    style={{ 
                        height: '38px', padding: '0 32px', borderRadius: '10px',
                        fontWeight: 800, fontSize: '13px', background: '#4f46e5',
                        border: 'none', boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)'
                    }}
                >
                    {admin ? 'Update Admin' : 'Create Admin'}
                </Button>
            </div>
        </Modal>
    );
};

export default AddAdminModal;
